import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { designShowcaseService } from "../services/designShowcaseService";
import {
  DesignShowcaseValuesInit,
  getDesignShowcaseCreateVariables,
  getDesignShowcaseValues,
  DesignShowcaseValuesType,
  DesignShowcaseErrorsType,
  designShowcaseDisplayId,
} from "./DesignShowcaseCommonStore";
import { DesignShowcase, SortOrder } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getShowcaseCategoryValues,
  ShowcaseCategoryValuesType,
} from "../../showcaseCategory/store/ShowcaseCategoryCommonStore";
import { isNil } from "../../../common/utils";
import { validateMask } from "../../common/utils";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { GridColumn } from "../../common/types";
import { ShowcaseLineCreateStore } from "../../showcaseLine/store";
import { apiUploadFile } from "../../../api/apiClient";
import { uploadFailureReason } from "../../../common/utils";
import { GridChangeSet } from "../../common/types";
import { confirm } from "../../../common/services/confirmService";
import { getApiAsync } from "../../../api";
import { ShowcaseStatus } from "../../../api/graphql";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { showcaseCategoryService } from "../../showcaseCategory/services/showcaseCategoryService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class DesignShowcaseCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: DesignShowcaseErrorsType = {};
  service = designShowcaseService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    identification: { open: true, errors: [] },
    classification: { open: true, errors: [] },
    numbers: { open: true, errors: [] },
    dates: { open: true, errors: [] },
    flags: { open: true, errors: [] },
    contact: { open: true, errors: [] },
    documents: { open: true, errors: [] },
    richContent: { open: true, errors: [] },
    advanced: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: DesignShowcase | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = DesignShowcaseValuesInit();
  codeError: string | undefined;
  nameError: string | undefined;
  summaryError: string | undefined;
  statusError: string | undefined;
  categoryError: string | undefined;
  categoryService = showcaseCategoryService;
  categorySearchValue: string = "";
  categoryOptions: ShowcaseCategoryValuesType[] = [];
  relatedCategoriesError: string | undefined;
  relatedCategoriesService = showcaseCategoryService;
  relatedCategoriesSearchValue: string = "";
  relatedCategoriesOptions: ShowcaseCategoryValuesType[] = [];
  appliedStatusesError: string | undefined;
  quantityError: string | undefined;
  durationInYearsError: string | undefined;
  amountError: string | undefined;
  ratioError: string | undefined;
  startDateError: string | undefined;
  expiresAtError: string | undefined;
  registeredAtError: string | undefined;
  activeError: string | undefined;
  highlightedError: string | undefined;
  phoneError: string | undefined;
  taxIdError: string | undefined;
  attachmentError: string | undefined;
  anyFileError: string | undefined;
  avatarError: string | undefined;
  keywordsError: string | undefined;
  bodyError: string | undefined;
  metadataError: string | undefined;
  internalNoteError: string | undefined;
  apiKeyError: string | undefined;
  linesError: string | undefined;
  linesVisibleColumns: GridColumn[];
  linesCreateStore: ShowcaseLineCreateStore;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<DesignShowcaseValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.linesVisibleColumns = [
      { name: "concept", title: this.langStore.ShowcaseLineConceptLabel },
      { name: "quantity", title: this.langStore.ShowcaseLineQuantityLabel },
      { name: "unitPrice", title: this.langStore.ShowcaseLineUnitPriceLabel },
    ];
    this.linesCreateStore = new ShowcaseLineCreateStore(
      rootStore,
      "lines_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<DesignShowcaseValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<DesignShowcaseValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return designShowcaseDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.DesignShowcaseSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  viewFormCategory() {
    const id = this.entityValues.showCategory?.id;
    if (id) {
      this.rootStore.openOrPushOverlay("showcaseCategory", id, "view");
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity
      if (params.callerData?.entityName === "ShowcaseLine") {
        this.linesCreateStore.isWaitingForCallback = false;
        this.linesCreateStore.callbackReturnPath = null;
        yield this.linesCreateStore.returnedData(params);
        return;
      }

      if (params.callerData?.fieldName === "category") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showCategory = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.showcaseCategoryOne({
            id: params.returnData.resultId,
          });
          const showcaseCategory = result.showcaseCategoryOne;
          this.entityValues.showCategory = {
            displayId: showcaseCategory.displayId,
            id: showcaseCategory.id,
          };
          this.setCategoryOptions([
            getShowcaseCategoryValues(showcaseCategory, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "relatedCategories") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showRelatedCategories.push({
            displayId,
            id: params.returnData.entity.id,
          });
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.showcaseCategoryOne({
            id: params.returnData.resultId,
          });
          const showcaseCategory = result.showcaseCategoryOne;
          this.entityValues.showRelatedCategories.push({
            displayId: showcaseCategory.displayId,
            id: showcaseCategory.id,
          });
          this.setRelatedCategoriesOptions([
            getShowcaseCategoryValues(showcaseCategory, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupDesignShowcaseLabel,
        errors: this.getCardErrors("null"),
      },
      {
        id: "identification",
        label: this.rootStore.langStore.groupDesignShowcaseIdentificationLabel,
        errors: this.getCardErrors("identification"),
      },
      {
        id: "classification",
        label: this.rootStore.langStore.groupDesignShowcaseClassificationLabel,
        errors: this.getCardErrors("classification"),
      },
      {
        id: "numbers",
        label: this.rootStore.langStore.groupDesignShowcaseNumbersLabel,
        errors: this.getCardErrors("numbers"),
      },
      {
        id: "dates",
        label: this.rootStore.langStore.groupDesignShowcaseDatesLabel,
        errors: this.getCardErrors("dates"),
      },
      {
        id: "flags",
        label: this.rootStore.langStore.groupDesignShowcaseFlagsLabel,
        errors: this.getCardErrors("flags"),
      },
      {
        id: "contact",
        label: this.rootStore.langStore.groupDesignShowcaseContactLabel,
        errors: this.getCardErrors("contact"),
      },
      {
        id: "documents",
        label: this.rootStore.langStore.groupDesignShowcaseDocumentsLabel,
        errors: this.getCardErrors("documents"),
      },
      {
        id: "richContent",
        label: this.rootStore.langStore.groupDesignShowcaseRichContentLabel,
        errors: this.getCardErrors("richContent"),
      },
      {
        id: "advanced",
        label: this.rootStore.langStore.groupDesignShowcaseAdvancedLabel,
        errors: this.getCardErrors("advanced"),
      },
    ];
  }

  getCodeValue(): string {
    return this.entityValues.code;
  }

  setCodeValue(value: string) {
    this.entityValues.code = value;
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  getSummaryValue(): string {
    return this.entityValues.summary;
  }

  setSummaryValue(value: string) {
    this.entityValues.summary = value;
  }

  getStatusValue(): ShowcaseStatus {
    return this.entityValues.status;
  }

  setStatusValue(value: ShowcaseStatus) {
    this.entityValues.status = value;
  }

  getStatusOptions() {
    return this.langStore.ShowcaseStatusOptions;
  }

  setCategoryOptions(values: ShowcaseCategoryValuesType[]) {
    this.categoryOptions = values;
  }

  *loadCategoryValues(): any {
    let where: any = null;
    if (this.categorySearchValue) {
      const searchFilter = {
        name: { contains: this.categorySearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.showcaseCategoryListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ name: SortOrder.Asc }],
    });
    this.setCategoryOptions(
      (result.showcaseCategoryListWithOffset || []).map((x: any) =>
        getShowcaseCategoryValues(x, this.langStore)
      )
    );
  }

  getCategoryOptions() {
    return this.categoryOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setCategoryValue(value: string) {
    this.categorySearchValue = value;
    yield this.loadCategoryValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedCategory(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewShowcaseCategory(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "category"
      );
    } else {
      if (value) {
        this.entityValues.showCategory = value;
        this.entityValues.__category = value.displayId;
      }
    }
  }

  setRelatedCategoriesOptions(values: ShowcaseCategoryValuesType[]) {
    this.relatedCategoriesOptions = values;
  }

  *loadRelatedCategoriesValues(): any {
    let where: any = null;
    if (this.relatedCategoriesSearchValue) {
      const searchFilter = {
        name: {
          contains: this.relatedCategoriesSearchValue,
          mode: "insensitive",
        },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.showcaseCategoryListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ name: SortOrder.Asc }],
    });
    this.setRelatedCategoriesOptions(
      (result.showcaseCategoryListWithOffset || []).map((x: any) =>
        getShowcaseCategoryValues(x, this.langStore)
      )
    );
  }

  getRelatedCategoriesOptions() {
    return this.relatedCategoriesOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setRelatedCategoriesValue(value: string) {
    this.relatedCategoriesSearchValue = value;
    yield this.loadRelatedCategoriesValues();
  }

  setSelectedRelatedCategories(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewShowcaseCategory(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "relatedCategories"
      );
    } else {
      this.entityValues.showRelatedCategories = values;
      this.entityValues.__relatedCategories = values.map((v) => v.displayId);
    }
  }

  getAppliedStatusesValue(): ShowcaseStatus[] {
    return this.entityValues.appliedStatuses;
  }

  setAppliedStatusesValue(value: ShowcaseStatus[]) {
    this.entityValues.appliedStatuses = value;
  }

  getAppliedStatusesOptions() {
    return this.langStore.ShowcaseStatusOptions;
  }

  getQuantityValue(): number {
    return this.entityValues.quantity;
  }

  setQuantityValue(value: number) {
    this.entityValues.quantity = value;
  }

  getDurationInYearsValue(): number | undefined {
    return this.entityValues.durationInYears;
  }

  setDurationInYearsValue(value: number | undefined) {
    this.entityValues.durationInYears = value;
  }

  getAmountValue(): number {
    return this.entityValues.amount;
  }

  setAmountValue(value: number) {
    this.entityValues.amount = value;
  }

  getRatioValue(): number | undefined {
    return this.entityValues.ratio;
  }

  setRatioValue(value: number | undefined) {
    this.entityValues.ratio = value;
  }

  getStartDateValue(): string | null {
    return this.entityValues.startDate;
  }

  setStartDateValue(value: string | null) {
    if (value !== null) {
      this.entityValues.startDate = value;
    }
  }

  getExpiresAtValue(): Date | null {
    return this.entityValues.expiresAt;
  }

  setExpiresAtValue(value: Date | null) {
    this.entityValues.expiresAt = value;
  }

  getRegisteredAtValue(): Date | null {
    return this.entityValues.registeredAt;
  }

  setRegisteredAtValue(value: Date | null) {
    this.entityValues.registeredAt = value;
  }

  getActiveValue(): boolean {
    return this.entityValues.active;
  }

  setActiveValue(value: boolean) {
    this.entityValues.active = value;
  }

  getHighlightedValue(): boolean {
    return this.entityValues.highlighted;
  }

  setHighlightedValue(value: boolean) {
    this.entityValues.highlighted = value;
  }

  getPhoneValue(): string {
    return this.entityValues.phone;
  }

  setPhoneValue(value: string) {
    this.entityValues.phone = value;
  }

  getTaxIdValue(): string {
    return this.entityValues.taxId;
  }

  setTaxIdValue(value: string) {
    this.entityValues.taxId = value;
  }

  attachmentRestoreFile(id: string) {
    if (this.entityValues.attachment.length > 1) {
      alert("No puede haber más de un archivo activo " + id);
      return;
    }
  }

  *attachmentUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__attachment_files];
    const previousName = this.entityValues.attachment;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__attachment_files.push(newFile);
    this.entityValues.attachment = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__attachment_files = previousFiles;
      this.entityValues.attachment = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("attachment");
      } else {
        this.setFieldError(
          "attachment",
          this.langStore.DesignShowcaseAttachmentLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("attachment");
    const fileIndex = this.entityValues.__attachment_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__attachment_files[fileIndex].ticket = outcome.ticket;
    }
  }

  attachmentCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__attachment_files) {
        if (file.id === id) {
          toRemove = index;
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__attachment_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  anyFileRestoreFile(id: string) {
    if (this.entityValues.anyFile.length > 1) {
      alert("No puede haber más de un archivo activo " + id);
      return;
    }
  }

  *anyFileUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__anyFile_files];
    const previousName = this.entityValues.anyFile;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__anyFile_files.push(newFile);
    this.entityValues.anyFile = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__anyFile_files = previousFiles;
      this.entityValues.anyFile = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("anyFile");
      } else {
        this.setFieldError(
          "anyFile",
          this.langStore.DesignShowcaseAnyFileLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("anyFile");
    const fileIndex = this.entityValues.__anyFile_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__anyFile_files[fileIndex].ticket = outcome.ticket;
    }
  }

  anyFileCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__anyFile_files) {
        if (file.id === id) {
          toRemove = index;
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__anyFile_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  *avatarUploadBlob(blob: Blob): any {
    const previousFiles = [...this.entityValues.__avatar_files];
    const previousName = this.entityValues.avatar;
    const file = new File([blob], "avatar.jpg", {
      type: blob.type || "image/jpeg",
    });
    const fileId = `avatar_${Date.now()}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__avatar_files = [newFile];
    this.entityValues.avatar = file.name;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__avatar_files = previousFiles;
      this.entityValues.avatar = previousName;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("avatar");
      } else {
        this.setFieldError(
          "avatar",
          this.langStore.DesignShowcaseAvatarLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("avatar");
    const fileIndex = this.entityValues.__avatar_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__avatar_files[fileIndex].ticket = outcome.ticket;
    }
  }

  avatarDelete() {
    this.entityValues.__avatar_files = [];
    this.entityValues.avatar = "";
  }

  getKeywordsValue(): string[] {
    return this.entityValues.keywords;
  }

  setKeywordsValue(value: string[]) {
    this.entityValues.keywords = value;
  }

  getBodyValue(): string {
    return this.entityValues.body;
  }

  setBodyValue(value: string) {
    this.entityValues.body = value;
  }

  getMetadataValue(): string {
    return this.entityValues.metadata;
  }

  setMetadataValue(value: string) {
    this.entityValues.metadata = value;
  }

  getInternalNoteValue(): string {
    return this.entityValues.internalNote;
  }

  setInternalNoteValue(value: string) {
    this.entityValues.internalNote = value;
  }

  getApiKeyValue(): string {
    return this.entityValues.apiKey;
  }

  setApiKeyValue(value: string) {
    this.entityValues.apiKey = value;
  }

  linesLoadRow(row: any) {
    this.linesCreateStore.loadValuesFromRow(row);
  }

  linesRestore(id: string) {
    const lines = this.entityValues.showLines.filter((x) => x.id === id)[0];
    if (lines) {
      lines.__status__ = restoredStatus(lines.__status__);
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  linesValidateChanges(_changes: GridChangeSet) {
    const errors = this.linesCreateStore.validateData(
      this.linesCreateStore.entityValues
    );

    this.linesCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  linesCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.linesCreateStore.entityValues;
      const lines = this.entityValues.showLines;
      for (let index = 0; index < this.entityValues.showLines.length; index++) {
        if (lines[index].id === values.id) {
          lines[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.linesCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.linesCreateStore.entityValues;
      if (!this.entityValues.showLines) {
        this.entityValues.showLines = [];
      }
      this.entityValues.showLines = [
        ...this.entityValues.showLines,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.linesCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const lines = this.entityValues.showLines;
      for (let index = 0; index < this.entityValues.showLines.length; index++) {
        if (lines[index].id === id) {
          if (isOriginalCrudStatus(lines[index].__status__)) {
            lines[index] = {
              ...lines[index],
              __status__: deleteStatus(lines[index].__status__),
            };
          } else {
            this.entityValues.showLines = this.entityValues.showLines.filter(
              (x) => x.id !== id
            );
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.lines);
    //   this.setCRUDRowsFromValues(newChanges, this.linesCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.lines = getNewCRUDRows(newChanges, this.entityValues.lines, this.uniqueCounter++);
    // }
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as DesignShowcaseErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as DesignShowcaseErrorsType;
  }

  *fileDelete(ticket: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    yield api.deleteUploadedFile({ ticket });
  }

  *addNewShowcaseCategory(
    notFound: string | undefined,
    callerFieldName?: string
  ): any {
    // QuickCreate checks for fields with quickCreate enabled
    if (notFound && callerFieldName === "category") {
      const confirmed = yield confirm(
        this.langStore.quickCreateConfirmTitle(
          this.langStore.ShowcaseCategorySingular
        ),
        this.langStore.quickCreateConfirmDescription(
          notFound,
          this.langStore.ShowcaseCategorySingular
        )
      );
      if (!confirmed) return;

      try {
        const api = yield getApiAsync(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.showcaseCategoryCreate({ name: notFound });

        if (result.showcaseCategoryCreate) {
          this.entityValues.showCategory = {
            id: result.showcaseCategoryCreate.id,
            displayId: result.showcaseCategoryCreate.displayId || notFound,
          };
          this.entityValues.__category =
            result.showcaseCategoryCreate.displayId || notFound;
        }
      } catch (error) {
        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      }
      return;
    }

    // Fallback to navigation for other fields
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "DesignShowcase",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/showcaseCategory/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = DesignShowcaseValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      identification: { open: true, errors: [] },
      classification: { open: true, errors: [] },
      numbers: { open: true, errors: [] },
      dates: { open: true, errors: [] },
      flags: { open: true, errors: [] },
      contact: { open: true, errors: [] },
      documents: { open: true, errors: [] },
      richContent: { open: true, errors: [] },
      advanced: { open: true, errors: [] },
    };
    this.categorySearchValue = "";
    this.categoryOptions = [];
    this.relatedCategoriesSearchValue = "";
    this.relatedCategoriesOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: DesignShowcaseValuesType): DesignShowcaseErrorsType {
    const errors: DesignShowcaseErrorsType = {};
    this.clearErrors();
    values.code = values.code.trim();
    if (values.code === "") {
      errors.code = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseCodeLabel,
      };
      this.setCardError("identification", errors.code.message);
    }
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseNameLabel,
      };
      this.setCardError("identification", errors.name.message);
    }

    if (!values.status) {
      errors.status = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseStatusLabel,
      };
      this.setCardError("classification", errors.status.message);
    }
    this.categoryError = undefined;
    if (values.showCategory === null || values.showCategory.id === "") {
      errors.category = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseCategoryLabel,
      };
      this.setCardError("classification", errors.category.message);
    }
    if (values.quantity > 9999) {
      errors.quantity = {
        message: this.langStore.maxValue + ": 9999",
        fieldLabel: this.langStore.DesignShowcaseQuantityLabel,
      };
      this.setCardError("numbers", errors.quantity.message);
    }
    if (values.durationInYears != null && values.durationInYears > 50) {
      errors.durationInYears = {
        message: this.langStore.maxValue + ": 50",
        fieldLabel: this.langStore.DesignShowcaseDurationInYearsLabel,
      };
      this.setCardError("numbers", errors.durationInYears.message);
    }
    if (values.durationInYears != null && values.durationInYears < 1) {
      errors.durationInYears = {
        message: this.langStore.minValue + ": 1",
        fieldLabel: this.langStore.DesignShowcaseDurationInYearsLabel,
      };
      this.setCardError("numbers", errors.durationInYears.message);
    }

    if (isNil(values.amount)) {
      errors.amount = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseAmountLabel,
      };
      this.setCardError("numbers", errors.amount.message);
    }
    if (values.ratio != null && values.ratio > 100) {
      errors.ratio = {
        message: this.langStore.maxValue + ": 100",
        fieldLabel: this.langStore.DesignShowcaseRatioLabel,
      };
      this.setCardError("numbers", errors.ratio.message);
    }

    if (isNil(values.startDate) || values.startDate === "") {
      errors.startDate = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseStartDateLabel,
      };
      this.setCardError("dates", errors.startDate.message);
    }

    if (isNil(values.active)) {
      errors.active = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseActiveLabel,
      };
      this.setCardError("flags", errors.active.message);
    }

    if (isNil(values.highlighted)) {
      errors.highlighted = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseHighlightedLabel,
      };
      this.setCardError("flags", errors.highlighted.message);
    }
    if (values.taxId && true) {
      if (!validateMask(values.taxId, { mask: "99-99999999-9" })) {
        errors.taxId = {
          message: this.langStore.maskedField("99-99999999-9"),
          fieldLabel: this.langStore.DesignShowcaseTaxIdLabel,
        };
      }
    }
    {
      const pendingUpload = values.__attachment_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.attachment = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.DesignShowcaseAttachmentLabel,
        };
        this.setCardError("documents", errors.attachment.message);
      }
    }
    {
      const pendingUpload = values.__anyFile_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.anyFile = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.DesignShowcaseAnyFileLabel,
        };
        this.setCardError("documents", errors.anyFile.message);
      }
    }
    {
      const pendingUpload = values.__avatar_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.avatar = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.DesignShowcaseAvatarLabel,
        };
        this.setCardError("documents", errors.avatar.message);
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "DesignShowcaseCreateStore"
    );
  }

  *duplicatesDesignShowcase(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const designShowcaseInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (designShowcaseInstance) {
      this.entityValues = getDesignShowcaseValues(
        designShowcaseInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(
        `/designShowcase/update/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewDesignShowcase(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewDesignShowcase(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      code: this.entityValues.code,
      name: this.entityValues.name,
      summary: this.entityValues.summary,
      status: this.entityValues.status,
      quantity: this.entityValues.quantity,
      durationInYears: this.entityValues.durationInYears,
      amount: this.entityValues.amount,
      ratio: this.entityValues.ratio,
      startDate: this.entityValues.startDate,
      expiresAt: this.entityValues.expiresAt,
      registeredAt: this.entityValues.registeredAt,
      active: this.entityValues.active,
      highlighted: this.entityValues.highlighted,
      phone: this.entityValues.phone,
      taxId: this.entityValues.taxId,
      body: this.entityValues.body,
      metadata: this.entityValues.metadata,
      internalNote: this.entityValues.internalNote,
      apiKey: this.entityValues.apiKey,
      category: this.entityValues.showCategory?.id || null,
      relatedCategories: (this.entityValues.showRelatedCategories || [])
        .map((x: any) => x.id)
        .sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getDesignShowcaseValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = DesignShowcaseValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const variables = getDesignShowcaseCreateVariables(this.entityValues);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.designShowcaseCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.designShowcaseCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.designShowcaseCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesDesignShowcase(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.code = initialDisplayId;
      }

      yield this.loadCategoryValues();
      yield this.loadRelatedCategoriesValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
